import { CheckIcon, CopyIcon, ExternalLinkIcon } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { PermissionsGate } from '@/modules/auth/components/permissions/PermissionsGate';
import { permissions } from '@/modules/auth/permissions';
import type { JobDetailApi } from '@/modules/jobs/queries/useJobQuery';
import { PUBLIC_SHORTLIST_SETTINGS_ID } from '@/modules/public-shortlist/constants';
import { useDisablePublicCandidateListMutation } from '@/modules/public-shortlist/mutations/useDisablePublicCandidateListMutation';
import { useEnablePublicCandidateListMutation } from '@/modules/public-shortlist/mutations/useEnablePublicCandidateListMutation';
import {
  getPublicShortlistUrl,
  isPublicShortlistAvailable,
} from '@/modules/public-shortlist/publicShortlist';
import { Alert, AlertDescription } from '@/modules/ui/components/alert/Alert';
import { Button } from '@/modules/ui/components/button/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/modules/ui/components/card/Card';
import { Input } from '@/modules/ui/components/input/Input';
import { Label } from '@/modules/ui/components/label/Label';
import { Switch } from '@/modules/ui/components/switch/Switch';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

interface PublicShortlistSettingsProps {
  job: JobDetailApi;
}

function formatExpiryDate(value?: string | null): string | null {
  if (!value) return null;

  return new Date(value).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export function PublicShortlistSettings({ job }: PublicShortlistSettingsProps) {
  const [copied, setCopied] = useState(false);

  const enableMutation = useEnablePublicCandidateListMutation();
  const disableMutation = useDisablePublicCandidateListMutation();

  const isEnabled = isPublicShortlistAvailable(job);
  const publicUrl = getPublicShortlistUrl(job);
  const isPending = enableMutation.isPending || disableMutation.isPending;
  const expiresAt = formatExpiryDate(job.publicCandidateListExpiresAt);

  const handleToggle = (checked: boolean) => {
    const mutation = checked ? enableMutation : disableMutation;

    mutation.mutate(job.id, {
      onSuccess: () => {
        toast.success(
          checked ? 'Public shortlist enabled' : 'Public shortlist disabled',
        );
      },
      onError: (error) => {
        toast.error(
          `Failed to update public shortlist: ${extractErrorMessage(error)}`,
        );
      },
    });
  };

  const handleCopyUrl = async () => {
    if (!publicUrl) return;

    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success('Public shortlist URL copied');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy URL');
    }
  };

  return (
    <Card id={PUBLIC_SHORTLIST_SETTINGS_ID}>
      <CardHeader>
        <CardTitle>Public shortlist</CardTitle>
        <CardDescription>
          Share a shortlist of candidates with sales prospects. The page is
          public and does not require login.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        <PermissionsGate permissions={[permissions.Jobs.Update]}>
          <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
            <div className="space-y-1">
              <Label htmlFor="public-shortlist-toggle" className="text-base">
                Enable public shortlist
              </Label>
              <p className="text-muted-foreground text-sm">
                Generates a shareable link at{' '}
                <span className="font-mono text-xs">
                  /:company/shortlist/:roleName?code=…
                </span>
              </p>
            </div>
            <Switch
              id="public-shortlist-toggle"
              checked={isEnabled}
              disabled={isPending}
              onCheckedChange={handleToggle}
            />
          </div>
        </PermissionsGate>

        {isEnabled && publicUrl ? (
          <div className="space-y-3">
            <Label htmlFor="public-shortlist-url">Public URL</Label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                id="public-shortlist-url"
                readOnly
                value={publicUrl}
                className="font-mono text-sm"
              />
              <div className="flex shrink-0 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCopyUrl}
                  className="flex-1 sm:flex-none"
                >
                  {copied ? (
                    <CheckIcon className="size-4" />
                  ) : (
                    <CopyIcon className="size-4" />
                  )}
                  Copy
                </Button>
                <Button type="button" variant="outline" asChild>
                  <a href={publicUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLinkIcon className="size-4" />
                    Preview
                  </a>
                </Button>
              </div>
            </div>
            {expiresAt ? (
              <p className="text-muted-foreground text-sm">
                Link expires {expiresAt}
              </p>
            ) : null}
            <p className="text-muted-foreground text-sm">
              Only candidates in the Shortlisted stage appear on the public
              page.
            </p>
          </div>
        ) : (
          <Alert>
            <AlertDescription>
              Enable the public shortlist to generate a shareable URL for
              outbound sales. Add candidates to the shortlist on the{' '}
              <Link
                to={`/jobs/${job.id}/candidates`}
                className="text-primary font-medium hover:underline"
              >
                candidates page
              </Link>{' '}
              first so prospects see real candidates.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
